import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../lib/api';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import { Calendar, ChevronLeft, Tag } from 'lucide-react';
import DOMPurify from 'dompurify';

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return ''; }
}

// Tiny markdown renderer — headings, paragraphs, bold, italic, lists, links, inline code
function renderMarkdown(md) {
  if (!md) return '';
  const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lines = md.split('\n');
  const out = [];
  let inList = false;
  const inline = (text) => text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-[#1a1a1a] rounded text-[#D4A843]">$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#D4A843] underline hover:text-[#E8C972]">$1</a>');
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith('### ')) { if (inList) { out.push('</ul>'); inList = false; } out.push(`<h3 class="text-xl font-bold mt-8 mb-3 text-white">${inline(escape(line.slice(4)))}</h3>`); }
    else if (line.startsWith('## ')) { if (inList) { out.push('</ul>'); inList = false; } out.push(`<h2 class="text-2xl font-bold mt-10 mb-4 text-[#D4A843]">${inline(escape(line.slice(3)))}</h2>`); }
    else if (line.startsWith('# ')) { if (inList) { out.push('</ul>'); inList = false; } out.push(`<h1 class="text-3xl font-bold mt-10 mb-4">${inline(escape(line.slice(2)))}</h1>`); }
    else if (/^[*-] /.test(line)) { if (!inList) { out.push('<ul class="list-disc ml-6 my-3 space-y-1 text-gray-300">'); inList = true; } out.push(`<li>${inline(escape(line.slice(2)))}</li>`); }
    else if (/^\d+\. /.test(line)) { if (!inList) { out.push('<ol class="list-decimal ml-6 my-3 space-y-1 text-gray-300">'); inList = true; } out.push(`<li>${inline(escape(line.replace(/^\d+\. /, '')))}</li>`); }
    else if (line.trim() === '') { if (inList) { out.push('</ul></ol>'.replace('</ul></ol>', inList ? (out.join('').endsWith('</li>') && out.some(o => o.startsWith('<ol')) ? '</ol>' : '</ul>') : '')); inList = false; } out.push(''); }
    else { if (inList) { out.push('</ul>'); inList = false; } out.push(`<p class="text-gray-300 leading-relaxed my-3">${inline(escape(line))}</p>`); }
  }
  if (inList) out.push('</ul>');
  return out.join('\n');
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const { data } = await api.get(`/api/marketing/blog/${slug}`);
        setPost(data);
        const { data: listData } = await api.get('/api/marketing/blog?limit=4');
        setRelated((listData.posts || []).filter(p => p.slug !== slug).slice(0, 3));
      } catch {
        setError(true);
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] text-white"><PublicNav /><p className="text-center text-sm text-gray-500 py-20">Loading…</p></div>;
  if (error || !post) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <PublicNav />
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <h1 className="text-3xl font-bold mb-4">Post not found</h1>
        <Link to="/blog" className="text-[#D4A843] hover:underline">← Back to the blog</Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <PublicNav />

      <article className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[#D4A843] mb-6" data-testid="blog-back"><ChevronLeft className="w-3 h-3" /> All posts</Link>

        {post.cover_image_url && (
          <div className="w-full h-64 md:h-80 overflow-hidden rounded-2xl mb-8 bg-[#111]">
            <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-3">
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(post.published_at)}</span>
          {post.tags?.map((t) => <span key={t} className="flex items-center gap-1 text-[#D4A843]"><Tag className="w-3 h-3" />{t}</span>)}
        </div>
        <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">{post.title}</h1>
        <p className="text-lg text-gray-400 leading-relaxed mb-8 border-l-2 border-[#D4A843] pl-4">{post.excerpt}</p>

        <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(post.body_markdown || '')) }} />

        <div className="mt-12 pt-8 border-t border-gray-800 text-xs text-gray-500">Written by <span className="text-white">{post.author_name}</span></div>
      </article>

      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-20">
          <h3 className="text-sm font-bold text-[#D4A843] uppercase tracking-wider mb-5">Keep reading</h3>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="block bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-[#D4A843]/40 transition-colors">
                <h4 className="text-sm font-bold">{p.title}</h4>
                <p className="text-[11px] text-gray-400 mt-2 line-clamp-2">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
